/**
 * Conversation right-click / actions context menu. Extracted verbatim
 * (behavior-preserving) from the `{contextMenu && (…)}` block in
 * layout/Sidebar.tsx.
 *
 * The orchestrator still owns the `contextMenu` open-state and the
 * `contextMenuRef` used by the click-outside hook; both are threaded in. The
 * render gate (`{contextMenu && <ConversationContextMenu … />}`) stays in the
 * orchestrator so mount/unmount semantics are unchanged.
 */

import type { RefObject } from 'react';
import {
  ArchiveBoxIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { PinIcon as PinOutlineIcon } from '@sidekickicons/react/24/outline';
import type { Conversation } from '@/types/conversations';
import { isConversationPinned } from '@/types/conversations';

interface ConversationContextMenuProps {
  contextMenu: { x: number; y: number; conversation: Conversation };
  contextMenuRef: RefObject<HTMLDivElement | null>;
  onRename: () => void;
  onPin: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

export function ConversationContextMenu({
  contextMenu,
  contextMenuRef,
  onRename,
  onPin,
  onArchive,
  onDelete,
}: ConversationContextMenuProps) {
  return (
    <div
      ref={contextMenuRef}
      className="conversation-context-menu"
      style={{
        position: 'fixed',
        top: contextMenu.y,
        left: contextMenu.x,
        zIndex: 999,
      }}
    >
      <button
        className="context-menu-item"
        onClick={onRename}
      >
        <PencilSquareIcon className="context-menu-item-icon" aria-hidden="true" />
        Rename
      </button>
      <button
        className="context-menu-item"
        onClick={onPin}
      >
        <PinOutlineIcon className="context-menu-item-icon" aria-hidden="true" />
        {isConversationPinned(contextMenu.conversation) ? 'Unpin Chat' : 'Pin Chat'}
      </button>
      <button
        className="context-menu-item"
        onClick={onArchive}
      >
        <ArchiveBoxIcon className="context-menu-item-icon" aria-hidden="true" />
        Archive
      </button>
      <button
        className="context-menu-item context-menu-item-danger"
        onClick={onDelete}
      >
        <TrashIcon className="context-menu-item-icon" aria-hidden="true" />
        Delete
      </button>
    </div>
  );
}
